import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentDeviceRentalRequestStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalDtoIn, PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate, formatPickupDay, isAwaitingPickup } from '@/helpers/paymentDevices';
import { accessoriesText } from '@/helpers/paymentDeviceAccessories';
import RentalFeeSummary from '@/pages/PaymentDevices/RentalFeeSummary';
import { LongRentalTag, MissingAccessoriesTag, OverdueTag, RequestStatusChip } from './DeviceStatusChip';
import styles from '../FundsManagement/FundBlade/FundBlade.module.css';

interface RentalRowProps {
  rental: PaymentDeviceRentalDtoIn;
  /** The row's first line - the device (in a fund's list) or the fund (in a device's history). */
  heading: ReactNode;
  actions?: ReactNode;
}

/** One rental in a blade list: who/what, its period, hand-over and return, and its status. */
export const RentalRow = ({ rental, heading, actions }: RentalRowProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.campaignRow}>
      <div className={styles.campaignSummary}>
        <div className={styles.campaignHeader}>
          <span className={styles.personName}>{heading}</span>
          <span className={`${styles.featureBadge} ${styles.featureBadgeOff}`}>{t(`paymentDevices.rentalStatus.${rental.status}`)}</span>
          {rental.isOverdue && <OverdueTag />}
          {rental.isLongRental && <LongRentalTag />}
          <MissingAccessoriesTag items={rental.missingAccessories} />
        </div>
        <span className={styles.personMeta}>
          <span>{t('paymentDevices.blade.rentalPeriod', { start: formatDeviceDate(rental.startDate), end: formatDeviceDate(rental.expectedReturnDate) })}</span>
          {rental.handedOverDate && (
            <>
              <span className={styles.metaDot}>•</span>
              <span>{t('paymentDevices.blade.handedOverOn', { date: formatDeviceDate(rental.handedOverDate) })}</span>
            </>
          )}
          {rental.returnedDate && (
            <>
              <span className={styles.metaDot}>•</span>
              <span>{t('paymentDevices.blade.returnedOn', { date: formatDeviceDate(rental.returnedDate) })}</span>
            </>
          )}
          {rental.returnedFaulty && (
            <>
              <span className={styles.metaDot}>•</span>
              <span>{t('paymentDevices.blade.returnedFaulty')}</span>
            </>
          )}
        </span>
        {(rental.handedOverAccessories?.length ?? 0) > 0 && (
          <span className={styles.personMeta}>{t('paymentDevices.accessories.line', { items: accessoriesText(rental.handedOverAccessories, t) })}</span>
        )}
        {rental.notes && <span className={styles.personMeta}>{rental.notes}</span>}
        {rental.fee && <RentalFeeSummary fee={rental.fee} compact />}
      </div>
      {actions && <div className={styles.rowActions}>{actions}</div>}
    </div>
  );
};

interface RequestRowProps {
  request: PaymentDeviceRentalRequestDtoIn;
  actions?: ReactNode;
}

/** One rental request in a blade list. */
export const RequestRow = ({ request, actions }: RequestRowProps) => {
  const { t } = useTranslation();
  const reason = request.status === PaymentDeviceRentalRequestStatus.Rejected
    ? [request.rejectionReason ? t(`paymentDevices.rejectionReason.${request.rejectionReason}`) : null, request.reviewNote].filter(Boolean).join(' · ')
    : request.reviewNote;
  const hold = isAwaitingPickup(request) && request.holdUntil
    ? t('paymentDevices.requestBlade.heldUntil', { day: formatPickupDay(request.holdUntil) })
    : request.expiredAt
      ? t('paymentDevices.requestBlade.expired', { day: formatPickupDay(request.holdUntil) })
      : null;
  return (
    <div className={styles.campaignRow}>
      <div className={styles.campaignSummary}>
        <div className={styles.campaignHeader}>
          <span className={styles.personName}>{t('paymentDevices.fundTab.request', { id: request.id })}</span>
          <RequestStatusChip status={request.status} />
        </div>
        <span className={styles.personMeta}>
          <span>{t('paymentDevices.blade.rentalPeriod', { start: formatDeviceDate(request.startDate), end: formatDeviceDate(request.expectedReturnDate) })}</span>
          <span className={styles.metaDot}>•</span>
          <span>{t(`paymentDevices.pickupArea.${request.pickupArea}`)}</span>
          <span className={styles.metaDot}>•</span>
          <span>{request.submittedByName || request.submittedByEmail}</span>
          {request.rental && (
            <>
              <span className={styles.metaDot}>•</span>
              <span dir="ltr">{t('paymentDevices.fundTab.device', { number: request.rental.deviceNumber })}</span>
            </>
          )}
        </span>
        {request.notes && <span className={styles.personMeta}>{request.notes}</span>}
        {reason && <span className={styles.personMeta}>{reason}</span>}
        {hold && <span className={`${styles.personMeta} font-semibold`}>{hold}</span>}
      </div>
      {actions && <div className={styles.rowActions}>{actions}</div>}
    </div>
  );
};

